import { useCountryOptions } from "@faire/retailer-visitor-shared/views/SignUp/_components/fields/useCountryOptions";
import { useLanguageOptions } from "@faire/retailer-visitor-shared/views/SignUp/_components/fields/useLanguageOptions";
import { localeStringToLocaleKey } from "@faire/web--source/common/consts/TWO_TO_THREE_LETTER_LANGUAGE_CODE";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { localeKeyToLocaleString } from "@faire/web--source/common/localization/localeKeyToLocaleString";
import Checkbox from "@faire/web--source/slate/Checkbox";
import { Dropdown, IOption } from "@faire/web--source/slate/Dropdown";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { useField } from "formik";
import React from "react";

interface ICountryFieldProps {
  disabled?: boolean;
  trackChange?: (name: string, value: string) => void;
}

export const CountryField: React.FC<ICountryFieldProps> = ({
  disabled,
  trackChange,
}) => {
  const [field, _, helpers] = useField("country");
  const { strictLocalize } = useStrictLocalization();
  const countryOptions = useCountryOptions();

  const handleSelect = (option: IOption<string>) => {
    helpers.setValue(option.value);
    trackChange?.("country", option.value);
  };

  return (
    <Dropdown
      disabled={disabled}
      label={strictLocalize({
        defaultMessage: "Country/Region",
      })}
      options={countryOptions}
      value={field.value}
      onSelect={handleSelect}
      data-test-id="country"
    />
  );
};

interface ILangFieldProps {
  country: string;
  trackChange?: (name: string, value: string) => void;
}

export const LangField: React.FC<ILangFieldProps> = ({
  country,
  trackChange,
}) => {
  const [field, _, helpers] = useField("locale_key");
  const { strictLocalize } = useStrictLocalization();
  const localeOptions = useLanguageOptions(country);

  const handleSelect = (option: IOption) => {
    helpers.setValue(localeStringToLocaleKey(option.value));
    trackChange?.("locale_key", option.value);
  };

  const value = field.value
    ? localeKeyToLocaleString({
        localeKey: field.value,
        dashFormat: false,
      })
    : undefined;

  return (
    <Dropdown
      label={strictLocalize({
        defaultMessage: "Language",
      })}
      options={localeOptions}
      value={value}
      onSelect={handleSelect}
      data-test-id="language"
    />
  );
};

interface IEmailFieldProps {
  disabled?: boolean;
}

export const EmailOptOutField: React.FC<IEmailFieldProps> = ({ disabled }) => {
  const [field, _, helpers] = useField("optedInToMarketingEmails");
  return (
    <Checkbox
      disabled={disabled}
      align="flex-start"
      checked={!field.value}
      onChange={() => {
        helpers.setValue(!field.value);
      }}
      data-test-id="receive-marketing-emails-checkbox"
    >
      <Typography>
        <StrictLocalMsg
          defaultMessage="Opt out of emails with the latest from Faire. You can change your preferences anytime."
          description={{
            text: "Copy next to a checkbox giving the retailer the option to opt out (e.g. unsubscribe) from receiveing the latest Faire news and offers.",
          }}
        />
      </Typography>
    </Checkbox>
  );
};
